import type { EngagementType } from '../../types';

/** The Customer Hub tab of each engagement type (P-01/C4 — "lll" is לל"ח). */
export const ENGAGEMENT_TAB_BY_TYPE: Record<EngagementType, string> = {
  project: 'projects',
  development: 'developments',
  service_contract: 'service-contracts',
  hour_bank: 'hour-banks',
  service_without_contract: 'lll',
};

const ENGAGEMENT_TYPE_ORDER: EngagementType[] = [
  'project', 'development', 'service_contract', 'hour_bank', 'service_without_contract',
];

/**
 * P-01/C4 (D-20) — dynamic Customer Hub tabs. The row holds what matters for
 * THIS customer: overview, contacts, one tab per engagement type the customer
 * actually has (any status that is not archived), then tasks, service
 * requests and documents. Everything else is behind "עוד", in this order:
 * the unused engagement types, quotes, contracts, work hours, billing,
 * profitability.
 *
 * P-07 (D-74 §6) — the "quotes" tab is "אפיונים והצעות". While the customer
 * is IN CHARACTERIZATION (an active "אפיון" card and no open engagement yet),
 * it joins the row right after contacts; once there is an engagement it goes
 * back behind "עוד" by itself (the flag comes from the server).
 *
 * `order` is the full tab order and `primary` the row; both feed
 * `splitCustomerTabs`. Permission filtering (contacts, billing,
 * profitability) stays with the caller — a tab it drops is simply absent.
 */
export function customerTabLayout(
  presentTypes: Iterable<EngagementType>,
  opts: { inCharacterization?: boolean } = {},
): { order: string[]; primary: string[] } {
  const present = new Set(presentTypes);
  const used = ENGAGEMENT_TYPE_ORDER.filter((ty) => present.has(ty)).map((ty) => ENGAGEMENT_TAB_BY_TYPE[ty]);
  const unused = ENGAGEMENT_TYPE_ORDER.filter((ty) => !present.has(ty)).map((ty) => ENGAGEMENT_TAB_BY_TYPE[ty]);
  const quotesUp = !!opts.inCharacterization;
  const primary = ['overview', 'contacts', ...(quotesUp ? ['quotes'] : []), ...used, 'tasks', 'service-requests', 'documents'];
  const order = [...primary, ...unused, ...(quotesUp ? [] : ['quotes']), 'contracts', 'work-hours', 'billing', 'profitability'];
  return { order, primary };
}

/**
 * Which Customer Hub tabs sit in the row and which go behind "עוד ▾"
 * (principle 0: 14 tabs in one row clipped the last one at 1440 px).
 *
 * - `row`: the primary tabs, in the hub's order, plus the open tab when it is
 *   not primary (the tablist always holds its selected tab).
 * - `more`: every tab that is not primary, in the hub's order (the open one
 *   included, so the menu can mark it as current).
 * Without `primary`, or when it names fewer than two tabs the viewer has,
 * every tab stays in the row and nothing goes behind "עוד".
 */
export function splitCustomerTabs<T extends { key: string }>(
  tabs: readonly T[],
  active: string,
  primary?: readonly string[],
): { row: T[]; more: T[] } {
  if (!primary) return { row: [...tabs], more: [] };
  const isPrimary = (tab: T) => primary.includes(tab.key);
  const more = tabs.filter((tab) => !isPrimary(tab));
  // One tab alone behind a menu is worse than a slightly longer row.
  if (more.length < 2 || tabs.filter(isPrimary).length < 2) return { row: [...tabs], more: [] };
  return { row: tabs.filter((tab) => isPrimary(tab) || tab.key === active), more };
}

/**
 * D-321 (QA-D321-3): the "עוד" button sits INSIDE the tablist as a tab. When the open tab is one of
 * the menu's, the button is the selected tab (aria-selected=true, tabindex 0, active style) and its
 * accessible name carries the open tab's name; otherwise it is an unselected tab that still opens the menu.
 */
export function moreTabAria(activeInMore: boolean): { role: 'tab'; 'aria-selected': boolean; tabIndex: number; className: string } {
  return {
    role: 'tab',
    'aria-selected': activeInMore,
    tabIndex: 0,
    className: activeInMore ? 'tab-active' : 'tab-inactive',
  };
}

/** Tabs that fit a phone row next to the "עוד" button without cutting any (QA-D321-3, 360/390 px). */
export const NARROW_ROW_TABS = 4;

/** On a narrow screen only the first few primary tabs stay in the row; the rest go behind "עוד". */
export function narrowPrimary(primary: readonly string[] | undefined, narrow: boolean): readonly string[] | undefined {
  return primary && narrow ? primary.slice(0, NARROW_ROW_TABS) : primary;
}
